import {
    Box,
    Divider,
    Flex,
    HStack,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import i18next from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import CanopyButton from '../../canopy-ui-components/components/CanopyButton/CanopyButton';
import IconButton from '../../canopy-ui-components/components/IconButton/IconButton';
import {
    changei18nLanguage,
    storeItem,
} from '../../canopy-ui-components/helpers/localStorage';
import { useCurrentColors } from '../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';
import { usePartnerPreferences } from '../../hooks/UsePartnerPreferences/UsePartnerPreferences';
import { useUserPreferences } from '../../hooks/UsePreferences/UsePreferences';
import LanguageSelector, {
    DEFAULT_LOCALE,
    ILabelValue,
} from './LanguageSelector';

function PreferencesModal({ t }: any) {
    const currentColors = useCurrentColors();
    const [userPreferences, updateUserPreferences] = useUserPreferences();
    const [partnerPreference, getLanguage] = usePartnerPreferences();
    const [selected, setSelected] = useState<ILabelValue>();
    const { isOpen, onOpen, onClose } = useDisclosure();

    useEffect(() => {
        // handle locale overriding
        const userLocale = userPreferences.data?.locale;
        const partnerLocale = partnerPreference.data?.defaultLocale;
        const storedLocale = i18next.language;
        let locale: any;

        if (userLocale) {
            locale = userLocale;
        } else if (storedLocale) {
            locale = storedLocale;
        } else if (partnerLocale) {
            locale = partnerLocale;
        } else {
            locale = DEFAULT_LOCALE;
        }
        setSelected(getLanguage(locale));
        // i18next.changeLanguage(locale);
        // storeItem('localeId', locale);
        changei18nLanguage(locale);
    }, [userPreferences.data?.locale, partnerPreference.data?.defaultLocale]);

    useEffect(() => {
        // open dialog if no user preference set for locale
        const { data, loading, error } = userPreferences;
        if (!loading && data && !data.locale && !error) {
            onOpen();
        }
    }, [userPreferences]);

    const handleClose = useCallback(() => {
        onClose();
        selected &&
            updateUserPreferences({
                locale: selected.value,
            });
    }, [selected, partnerPreference, updateUserPreferences]);

    const onLocaleChange = useCallback((locale: ILabelValue) => {
        if (locale) {
            setSelected(locale);
        }
    }, []);

    return (
        <Modal
            isCentered
            closeOnOverlayClick={false}
            isOpen={isOpen}
            size="3xl"
            onEsc={handleClose}
            onClose={handleClose}>
            <ModalOverlay
                style={{
                    backdropFilter: 'blur(5px)',
                }}
            />
            <ModalContent
                pt="12px"
                pb="12px"
                borderRadius={'4px'}
                backgroundColor={currentColors.componentBg}>
                <ModalHeader
                    color={currentColors.brand['10']}
                    fontSize="20px"
                    fontWeight="bold"
                    flex="-1"
                    padding="0px 1rem 0px 1rem"
                    display="flex"
                    justifyContent="space-between"
                    marginBottom="8px">
                    {t('ui.preferences')}
                    <ModalCloseButton
                        as={() => (
                            <IconButton
                                background="transparent"
                                cursor="pointer"
                                border="none"
                                display="inline-flex"
                                onClick={handleClose}
                                color={currentColors.brand['500']}
                                width="24px"
                                height="24px"
                                aria-label="remove"
                                icon="ActClose"
                                variant="ghost"
                            />
                        )}
                    />
                </ModalHeader>
                <ModalBody
                    padding="0 15px"
                    maxHeight={'60vh'}
                    overflow="auto">
                    <Box>
                        <Text
                            color={currentColors.brand['10']}
                            fontSize="15px"
                            mb="10px">
                            {t('select-preferred-language')}
                        </Text>
                        <Flex
                            direction="column"
                            width="50%">
                            <LanguageSelector
                                selectedLanguage={selected}
                                width={'100%'}
                                t={t}
                                onChange={onLocaleChange}
                            />
                        </Flex>
                        <Text
                            mt="16px"
                            color={currentColors.brand['400']}
                            fontSize="12px">
                            {t('default-language-change-hint')}
                        </Text>
                    </Box>
                </ModalBody>
                <ModalFooter
                    display="block"
                    padding="0 15px">
                    <Divider
                        mt="8px"
                        mb="8px"
                    />
                    <HStack
                        width="100%"
                        spacing="15px"
                        justifyContent="flex-end">
                        <CanopyButton
                            data-test="deleteButton"
                            fontSize="sm"
                            width="100px"
                            fontWeight="bold"
                            disabled={!selected}
                            onClick={handleClose}>
                            {t('save')}
                        </CanopyButton>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
export default PreferencesModal;
